/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Cell, Row } from "react-table";
import { EnvironmentAndSpends, Provider } from "~/generated/graphql";
import { CostForecastHeader } from "~/components";
import {
  EstateCostCell,
  ProviderCell,
  ResourceNameDetailsCell,
} from "~/components/Tables/Cells";
import { ApplicationEnvironmentCell } from "./cells";

type Estate = {
  id: string;
  provider: Provider;
  type?: string;
  category?: string;
  nordcloudId: string;
  cloudAccountId?: string;
  nid: string;
  name: string;
  environment: {
    name: string;
    id: string;
    nid: string;
    application: {
      nid: string;
      id: string;
      name: string;
    };
  };
  environments: EnvironmentAndSpends[];
  actualCosts: string;
  forecast: string;
  region: string;
  isWaste: boolean;
  reason: string;
  savingsAmount: string;
  savingsAmountCount: number;
  co2e: number;
  co2eSavings: number;
  assignments: {
    appCount: number;
    envCount: number;
  };
  count: number;
};

const applicationCell = ({ row }: Cell<Estate>) => {
  return (
    <ApplicationEnvironmentCell
      environment={row.original?.environment}
      assignments={row.original.assignments}
    />
  );
};

export const EstateListColumns = () => [
  {
    headeer: "",
    accessorKey: "provider",
    id: "provider",
    width: "3%",
    enableSorting: false,
    cell: ({ row }: Cell<Estate>) => (
      <ProviderCell provider={row.original.provider} />
    ),
  },
  {
    header: "Resource name / Resource Details",
    accessorKey: "nordcloudId",
    id: "NID",
    width: "16rem",
    cell: ({ row }: Cell<Estate>) => {
      const { type, name, id, nordcloudId, provider, category, region } =
        row.original;

      return (
        <ResourceNameDetailsCell
          id={id}
          nordcloudId={nordcloudId ?? id}
          name={name}
          type={type}
          category={category}
          provider={provider}
          region={region}
        />
      );
    },
  },
  {
    header: "Application / Environment",
    accessorKey: "application",
    id: "TYPE",
    disableSortBy: true,
    width: "13rem",
    cell: (row: Cell<Estate>) => applicationCell(row),
  },
  {
    header: <CostForecastHeader>Monthly Cost</CostForecastHeader>,
    accessorKey: "actualCosts",
    id: "COST",
    width: "6rem",
    cell: ({ row }: Cell<Estate>) => {
      const {
        id,
        isWaste,
        reason,
        actualCosts,
        savingsAmount,
        savingsAmountCount,
        co2e,
        co2eSavings,
      } = row.original;

      return (
        <EstateCostCell
          estateId={id}
          isWaste={isWaste}
          reason={reason}
          value={actualCosts}
          amount={savingsAmount}
          count={savingsAmountCount ?? 0}
          co2e={co2e}
          co2eSavings={co2eSavings}
          extraInfo={getExtraInfo(row)}
          costTooltipValue={getCostTooltipValue(row)}
        />
      );
    },
  },
  {
    header: <CostForecastHeader>Forecast</CostForecastHeader>,
    id: "FORECAST",
    accessorKey: "forecastCost",
    width: "6rem",
    cell: ({ row }: Cell<Estate>) => {
      const { id, isWaste, reason, forecast } = row.original;

      return (
        <EstateCostCell
          estateId={id}
          isWaste={isWaste}
          reason={reason}
          value={forecast}
          amount=""
          count={0}
        />
      );
    },
  },
];

function isGcpProject(provider: Provider, type?: string) {
  return provider === Provider.Gcp && type === "project";
}

function getExtraInfo(estate: Row<Estate>) {
  const { count, provider, type } = estate.original;
  return isGcpProject(provider, type) && count > 0 ? `${count} services` : "";
}

function getCostTooltipValue(estate: Row<Estate>) {
  const { provider, type } = estate.original;

  if (!isGcpProject(provider, type)) {
    return;
  }

  return "The cost of the project is highlighted in grey because IBM Multicloud Accelerator takes more granular service-level costs in all calculations. Click the number of services to see more details.";
}
